import { type FC, Suspense, useState, useEffect, lazy } from 'react';
import { graphql, useQueryLoader } from 'react-relay';
import { useSelector } from 'react-redux';
import classnames from 'classnames';

import { FormattedMessage } from 'dibs-react-intl';
import { ErrorBoundary } from 'dibs-error-tools/ErrorBoundary.tsx';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel.tsx';
import Dropdown, { alignConstants, sizeConstants } from '../../Dropdown/Dropdown.tsx';
import DropdownBody from '../../Dropdown/DropdownBody.tsx';
import DropdownLoadingContent from '../../Dropdown/DropdownLoadingContent.tsx';
import { UserNotificationsIcon } from './UserNotificationsIcon.tsx';
import { UserNotificationsMarkAllAsRead } from './UserNotificationsMarkAllAsRead.tsx';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker.tsx';

import { getUserSessionCountryCode } from 'dibs-regional-info/exports/regionalInfoHelpers.ts';
import { getPriceBookName } from 'dibs-cookie-jar';
import { stepInteractionConstants } from 'dibs-tracking';
import { trackGlobalNavEvent } from './userNotificationsTracking.ts';
import {
    isImprovePriceDropOfferNotificationVariant,
    trackImprovePriceDropOfferNotificationTest,
} from 'dibs-buyer-layout/src/helpers/notificationsTestHelpers.ts';

import { type UserNotificationsQuery } from './__generated__/UserNotificationsQuery.graphql.ts';
import { type UserNotificationsTabsQuery } from './__generated__/UserNotificationsTabsQuery.graphql.ts';

import styles from './UserNotifications.scss';
import dibsCss from 'dibs-css';

const UserNotificationsTabs = lazy(
    () => import(/* webpackChunkName: "UserNotificationsTabsLazy" */ './UserNotificationsTabs.tsx')
);

const MobileNotificationsModal = lazy(
    () =>
        import(
            /* webpackChunkName: "MobileNotificationsModalLazy" */ '../MobileNotificationsModal.tsx'
        )
);

export const userNotificationsQuery = graphql`
    query UserNotificationsQuery($userId: String!, $showPriceDropNotifications: Boolean) {
        viewer {
            ...UserNotificationsMarkAllAsRead_viewer
                @arguments(showPriceDropNotifications: $showPriceDropNotifications)
            ...UserNotificationsIcon_viewer
                @arguments(showPriceDropNotifications: $showPriceDropNotifications)
        }
    }
`;

export const userNotificationsTabsQuery = graphql`
    query UserNotificationsTabsQuery(
        $userId: String!
        $hasTradeUserId: Boolean!
        $userCountryCode: String!
        $priceBookName: String!
        $showPriceDropNotifications: Boolean
    ) {
        viewer {
            ...UserNotificationsTabs_viewer
                @arguments(showPriceDropNotifications: $showPriceDropNotifications)
        }
    }
`;

const noop = (): null => null;

const UserNotifications: FC<{
    userId: string;
    hasTradeUserId: boolean;
    isMobile?: boolean;
    hasRootPendingRefetch: boolean;
    showDelayDuration?: number;
    hideDelayDuration?: number;
}> = ({
    userId,
    hasTradeUserId,
    isMobile,
    hasRootPendingRefetch,
    showDelayDuration,
    hideDelayDuration,
}) => {
    const [showNotifications, setShowNotifications] = useState(false);
    const [hasSeenNotifications, setHasSeenNotifications] = useState(false);

    const [notificationsQueryRef, loadNotificationsQuery] =
        useQueryLoader<UserNotificationsQuery>(userNotificationsQuery);

    const [notificationsTabsQueryRef, loadNotificationsTabsQuery] =
        useQueryLoader<UserNotificationsTabsQuery>(userNotificationsTabsQuery);

    const isVipCuratedFolderTooltipVisible = useSelector<
        { header: { isVipCuratedFolderTooltipVisible: boolean } },
        boolean
    >(({ header }) => header.isVipCuratedFolderTooltipVisible);

    useEffect(() => {
        if (hasRootPendingRefetch) {
            return;
        }

        loadNotificationsQuery({
            userId,
            showPriceDropNotifications: isImprovePriceDropOfferNotificationVariant(),
        });
    }, [userId, loadNotificationsQuery, hasTradeUserId, hasRootPendingRefetch]);

    useEffect(() => {
        if (notificationsQueryRef) {
            loadNotificationsTabsQuery({
                userId,
                userCountryCode: getUserSessionCountryCode(),
                priceBookName: getPriceBookName(document.cookie) || 'DEFAULT',
                hasTradeUserId,
                showPriceDropNotifications: isImprovePriceDropOfferNotificationVariant(),
            });
        }
    }, [notificationsQueryRef, loadNotificationsTabsQuery, userId, hasTradeUserId]);

    const trackNotificationsDisplayed = (): void => {
        trackGlobalNavEvent({
            stepInteractionName:
                stepInteractionConstants.STEP_INTERACTION_GLOBAL_NAV_NOTIFICATIONS_CLICK,
            trigger: 'notification icon hover or click',
        });
    };

    const onShowNotifications = (): void => {
        if (showNotifications) {
            return;
        }

        setShowNotifications(true);
        trackNotificationsDisplayed();
    };

    const onToggleNotifications = (): void => {
        setShowNotifications(!showNotifications);
        trackNotificationsDisplayed();
    };

    const notificationsContent = (
        <>
            <VisibilityTracker
                key="hp-new-arrivals-ab-test-tracker"
                onVisibilityChange={({ isVisible }) => {
                    if (isVisible) {
                        trackImprovePriceDropOfferNotificationTest();
                    }
                }}
            />
            <div
                className={classnames(
                    dibsCss.flex,
                    dibsCss.itemsCenter,
                    dibsCss.justifyBetween,
                    dibsCss.pxSmall,
                    dibsCss.ptSmall,
                    { [styles.mobileHeader]: isMobile }
                )}
            >
                <HeadingLevel>
                    {Heading => (
                        <Heading
                            className={classnames(dibsCss.m0, dibsCss.textBodyLargeHeavy)}
                            dataTn="notifications-header-title"
                        >
                            <FormattedMessage
                                id="dbl.UserNotifications.title"
                                defaultMessage="Notifications"
                            />
                        </Heading>
                    )}
                </HeadingLevel>
                {!isMobile && (
                    <Suspense fallback={null}>
                        {notificationsQueryRef && (
                            <UserNotificationsMarkAllAsRead
                                queryRef={notificationsQueryRef}
                                userId={userId}
                                hasTradeUserId={hasTradeUserId}
                            />
                        )}
                    </Suspense>
                )}
            </div>
            {!notificationsTabsQueryRef && <DropdownLoadingContent />}
            {notificationsTabsQueryRef && (
                <Suspense fallback={<DropdownLoadingContent />}>
                    <UserNotificationsTabs
                        queryRef={notificationsTabsQueryRef}
                        hasTradeUserId={hasTradeUserId}
                        userId={userId}
                        hasSeenNotifications={hasSeenNotifications}
                        setHasSeenNotifications={() => setHasSeenNotifications(true)}
                    />
                </Suspense>
            )}
        </>
    );

    if (isMobile) {
        return (
            <ErrorBoundary fallback={noop}>
                <UserNotificationsIcon
                    isMobile
                    queryRef={notificationsQueryRef}
                    onClick={onShowNotifications}
                    isOpen={showNotifications}
                />
                {showNotifications && (
                    <Suspense fallback={null}>
                        <MobileNotificationsModal
                            onClose={() => setShowNotifications(false)}
                            isMwDesignIterationVariant
                        >
                            {notificationsContent}
                        </MobileNotificationsModal>
                    </Suspense>
                )}
            </ErrorBoundary>
        );
    }

    return (
        <ErrorBoundary fallback={noop}>
            {isVipCuratedFolderTooltipVisible ? (
                <UserNotificationsIcon
                    queryRef={notificationsQueryRef}
                    isOpen={undefined}
                    ariaDisabled
                    onClick={noop}
                />
            ) : (
                <div className={classnames(dibsCss.flex, dibsCss.hFull)}>
                    <Dropdown
                        align={alignConstants.ALIGN_RIGHT}
                        size={sizeConstants.SIZE_MEDIUM_LARGE}
                        onShow={onShowNotifications}
                        onHide={() => setShowNotifications(false)}
                        dropdownClassName={styles.dropdown}
                        forceOpen={showNotifications}
                        showDelayDuration={showDelayDuration}
                        hideDelayDuration={hideDelayDuration}
                    >
                        <UserNotificationsIcon
                            isOpen={showNotifications}
                            queryRef={notificationsQueryRef}
                            onClick={onToggleNotifications}
                        />
                        {showNotifications && <DropdownBody>{notificationsContent}</DropdownBody>}
                    </Dropdown>
                </div>
            )}
        </ErrorBoundary>
    );
};

export { UserNotifications };
